import React, { useRef } from "react";

/**
 * Retry until the callback returns a truthy value or resolves to a truthy value.
 * Ideally used for waiting for stores to catch up/rerender, but could probably be used for network calls.
 * @param {() => boolean|Promise<boolean>} callback - Should return a boolean or a promise that resolves to a boolean. Truthy = success, falsy = retry.
 * @param {() => void=} maxRetryCallback - Called when max retries are reached
 * @param {number=} maxRetries - Maximum number of retries
 * @returns {[() => void, () => void]} - [startRetries, reset]
 */
export const useRetry = (callback, maxRetryCallback, maxRetries = 5) => {
  const retryCountRef = useRef(0);

  const reset = () => {
    retryCountRef.current = 0;
  };

  const startRetries = async () => {
    const success = await Promise.resolve(callback());

    if (success) {
      reset();
      return;
    }

    retryCountRef.current += 1;
    Promise.resolve()
      .then(() => {
        if (retryCountRef.current < maxRetries) {
          startRetries();
        } else {
          maxRetryCallback?.();
          reset();
        }
      })
      .catch(() => {
        reset();
      });
  };

  return [startRetries, reset];
};
